import { useEffect, useMemo, useSyncExternalStore } from "react";
import { deviceLiveStatus, getLiveSocket } from "../utils/liveSocket";

const ALL = "*";
const SUBSCRIBE_FLUSH_MS = 50;
const CLOCK_MS = 5000;

function toIdList(deviceIds) {
  if (deviceIds == null) return [];
  const list = Array.isArray(deviceIds) ? deviceIds : [deviceIds];
  return list.map((id) => Number(id)).filter((id) => !Number.isNaN(id));
}

export function mergeLiveMessage(prev, message) {
  if (!message || typeof message !== "object") return prev;

  if (message.type === "snapshot" && Array.isArray(message.devices)) {
    const next = { ...prev };
    message.devices.forEach((device) => {
      if (device && device.deviceId != null) {
        next[device.deviceId] = device;
      }
    });
    return next;
  }

  if (message.type === "device_update" && message.deviceId != null) {
    return { ...prev, [message.deviceId]: message };
  }

  return prev;
}

let liveById = {};
let connected = false;
const listeners = new Map();
const retained = new Map();
let socketUnsub = null;
let flushTimer = null;

let clockNow = Date.now();
const clockListeners = new Set();
let clockTimer = null;
let clockRetainCount = 0;

function emit(id) {
  const set = listeners.get(id);
  if (set) set.forEach((fn) => fn());
}

export function applyLiveMessage(message) {
  if (message?.type === "connection") {
    connected = Boolean(message.connected);
    emit(ALL);
    return liveById;
  }

  const prev = liveById;
  const next = mergeLiveMessage(prev, message);
  if (next === prev) return prev;
  liveById = next;

  if (message.type === "snapshot" && Array.isArray(message.devices)) {
    message.devices.forEach((device) => {
      if (device && device.deviceId != null) emit(Number(device.deviceId));
    });
  } else if (message.type === "device_update" && message.deviceId != null) {
    emit(Number(message.deviceId));
  }
  emit(ALL);
  return next;
}

function subscribeKey(key, onChange) {
  let set = listeners.get(key);
  if (!set) {
    set = new Set();
    listeners.set(key, set);
  }
  set.add(onChange);
  return () => {
    set.delete(onChange);
    if (set.size === 0) listeners.delete(key);
  };
}

function flushSubscribe() {
  flushTimer = null;
  if (socketUnsub) {
    socketUnsub();
    socketUnsub = null;
  }
  const ids = Array.from(retained.keys());
  if (!ids.length) return;
  socketUnsub = getLiveSocket().subscribe(ids, applyLiveMessage);
}

function scheduleFlush() {
  if (flushTimer) clearTimeout(flushTimer);
  flushTimer = setTimeout(flushSubscribe, SUBSCRIBE_FLUSH_MS);
}

export function retainLiveIds(deviceIds) {
  const ids = toIdList(deviceIds);
  ids.forEach((id) => {
    retained.set(id, (retained.get(id) || 0) + 1);
  });
  scheduleFlush();
  return () => {
    ids.forEach((id) => {
      const next = (retained.get(id) || 1) - 1;
      if (next <= 0) retained.delete(id);
      else retained.set(id, next);
    });
    scheduleFlush();
  };
}

function retainClock() {
  clockRetainCount += 1;
  if (!clockTimer) {
    clockTimer = setInterval(() => {
      clockNow = Date.now();
      clockListeners.forEach((fn) => fn());
    }, CLOCK_MS);
  }
  return () => {
    clockRetainCount -= 1;
    if (clockRetainCount <= 0 && clockTimer) {
      clearInterval(clockTimer);
      clockTimer = null;
    }
  };
}

export function getLiveSnapshot() {
  return liveById;
}

export function resetLiveStoreForTests() {
  liveById = {};
  connected = false;
  listeners.clear();
  retained.clear();
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  if (socketUnsub) {
    socketUnsub();
    socketUnsub = null;
  }
  clockListeners.clear();
  clockRetainCount = 0;
  if (clockTimer) {
    clearInterval(clockTimer);
    clockTimer = null;
  }
  clockNow = Date.now();
}

export function useLiveDevices(deviceIds) {
  const idsKey = (Array.isArray(deviceIds) ? deviceIds : [deviceIds])
    .filter((id) => id != null && id !== "")
    .join(",");
  const ids = useMemo(() => toIdList(idsKey ? idsKey.split(",") : []), [idsKey]);

  useEffect(() => {
    if (!ids.length) return undefined;
    return retainLiveIds(ids);
  }, [ids]);

  const byId = useSyncExternalStore(
    (onStoreChange) => subscribeKey(ALL, onStoreChange),
    () => liveById
  );
  const isConnected = useSyncExternalStore(
    (onStoreChange) => subscribeKey(ALL, onStoreChange),
    () => connected
  );

  return { byId, connected: isConnected };
}

export function useLiveDevice(deviceId, { watchStatus = false } = {}) {
  const numericId = Number(deviceId);
  const valid = deviceId != null && deviceId !== "" && !Number.isNaN(numericId);

  useEffect(() => {
    if (!valid) return undefined;
    return retainLiveIds([numericId]);
  }, [valid, numericId]);

  useEffect(() => {
    if (!watchStatus) return undefined;
    return retainClock();
  }, [watchStatus]);

  const payload = useSyncExternalStore(
    (onStoreChange) => {
      if (!valid) return () => {};
      return subscribeKey(numericId, onStoreChange);
    },
    () => (valid ? liveById[numericId] : undefined)
  );

  const now = useSyncExternalStore(
    (onStoreChange) => {
      if (!watchStatus) return () => {};
      clockListeners.add(onStoreChange);
      return () => clockListeners.delete(onStoreChange);
    },
    () => (watchStatus ? clockNow : 0)
  );

  return {
    payload,
    data: payload?.data,
    units: {
      flow_unit: payload?.flow_unit,
      level_unit: payload?.level_unit,
      level_range_max_m: payload?.level_range_max_m,
      total_unit: payload?.total_unit,
    },
    status: deviceLiveStatus(payload?.data?.date_time, watchStatus ? now : Date.now()),
    connected,
  };
}
